/**
 * i18n runtime for the User Portal.
 *
 * Catalogs are BUNDLED, not fetched: this app is served from a container behind
 * the customer's own network and a runtime catalog fetch would add a failure mode
 * that renders the UI in message ids if it ever 404s.
 */
import { i18n } from '@lingui/core';

// The .po EXTENSION is required. @lingui/vite-plugin resolves and compiles the
// PO file itself, so the import must name it; without the extension Vite looks
// for a module called "messages" and fails with "Failed to resolve import".
import { messages as enUS } from '../locales/en-US/messages.po';
import { messages as esCO } from '../locales/es-CO/messages.po';

export const SUPPORTED_LOCALES = ['en-US', 'es-CO'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'en-US';

i18n.load({ 'en-US': enUS, 'es-CO': esCO });
i18n.activate(DEFAULT_LOCALE);

/**
 * Coerce anything to a locale this build can serve.
 *
 * Fails to en-US, deliberately. An unknown or malformed tag must never leave the
 * UI in an unrenderable state, and en-US is the source locale so it always
 * renders.
 */
export function normalizeLocale(value: unknown): SupportedLocale {
  if (typeof value !== 'string') return DEFAULT_LOCALE;
  const tag = value.trim();
  return (SUPPORTED_LOCALES as readonly string[]).includes(tag)
    ? (tag as SupportedLocale)
    : DEFAULT_LOCALE;
}

/**
 * Activate a locale and stamp <html lang>, which is what a screen reader and the
 * browser's own hyphenation read.
 */
export function activateLocale(value: unknown): SupportedLocale {
  const tag = normalizeLocale(value);
  i18n.activate(tag);
  if (typeof document !== 'undefined') document.documentElement.lang = tag;
  return tag;
}

export { i18n };
